import React, { useCallback, useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import { BreakdownsResult, EMetric } from 'api/useAnalytics';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { AnalyticsBarsBox } from 'components/AnalyticsBox/AnalyticsBarsBox/AnalyticsBarsBox';
import { AnalyticsTrendsBox } from 'components/AnalyticsBox/AnalyticsTrendsBox/AnalyticsTrendsBox';
import { Flex } from 'components/ui/flex';

import AnalyticsBusinessTabHeader from './AnalyticsBusinessTabHeader/AnalyticsBusinessTabHeader';
import { useAnalyticsData } from './hooks/use-analytics-data';
import { useXAxisDateFormat } from './hooks/useXAxisDateFormat';
import { useAnalyticsChartData } from './useAnalyticsChartData.hook';
import {
  BUSINESS_ANALYTICS,
  BUSINESS_BREAKDOWNS_ANALYTICS,
  DEFAULT_DATE_RANGE,
  getWholeRange,
  isDateStringHourly
} from './utils';

const isHiddenMetric = (metricName: EMetric, firstTrendDate?: string): boolean => {
  if (metricName === EMetric.TOTAL_REVENUE) return true;
  if (metricName === EMetric.DAU && firstTrendDate && isDateStringHourly(firstTrendDate)) return true;
  return false;
};

const AnalyticsBusinessTab: React.FC = () => {
  const { publisherId } = useParams();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [previousDateRange, setPreviousDateRange] = useState<
    DateRange | undefined
  >();
  const [attributeFilters, setAttributeFilters] = useState<
    Record<string, string[]>
  >({});

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);
  const apiPreviousDateRange = useMemo(
    () => getWholeRange(previousDateRange),
    [previousDateRange]
  );

  const { analytics, previousAnalytics, isLoading } = useAnalyticsData({
    apiDateRange,
    apiPreviousDateRange,
    publisherId,
    statsMetrics: BUSINESS_ANALYTICS,
    trendsMetrics: BUSINESS_ANALYTICS,
    breakdownsMetrics: BUSINESS_BREAKDOWNS_ANALYTICS,
    attributeFilters
  });

  const businessMetrics = useMemo(() => {
    const firstTrendDate = analytics.getTrends?.data?.[0]?.values?.[0]?.date;
    return BUSINESS_ANALYTICS.filter(
      (metricName) => !isHiddenMetric(metricName, firstTrendDate)
    );
  }, [analytics.getTrends?.data]);

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    setPreviousDateRange(undefined);
  };

  const {
    [EMetric.TOTAL_REVENUE]: totalRevenueAnalytics,
    ...formattedAnalytics
  } = useAnalyticsChartData({
    stats: analytics.getAnalytics.data,
    trends: analytics.getTrends.data,
    previousStats: previousAnalytics.getAnalytics.data,
    previousTrends: previousAnalytics.getTrends.data,
    previousDateRange: previousDateRange,
    currentDateRange: dateRange
  });

  const xAxisDateFormat = useXAxisDateFormat(analytics);

  const handleAttributeFiltersChange = useCallback(
    (filters: Record<string, string[]>) => {
      setAttributeFilters(filters);
    },
    []
  );

  return (
    <Flex className="pb-[57px]">
      <AnalyticsBusinessTabHeader
        publisherId={publisherId}
        dateRange={dateRange}
        previousDateRange={previousDateRange}
        onDateRangeChange={handleDateRangeChange}
        onPreviousDateRangeChange={setPreviousDateRange}
        onClearPreviousDateRange={() => setPreviousDateRange(undefined)}
        onAttributeFiltersChange={handleAttributeFiltersChange}
      />
      <div className="flex flex-col gap-4">
        <AnalyticsTrendsBox
          key={EMetric.TOTAL_REVENUE}
          metricName={EMetric.TOTAL_REVENUE}
          stat={totalRevenueAnalytics?.stat}
          currentTrends={totalRevenueAnalytics?.currentTrends ?? []}
          previousTrends={totalRevenueAnalytics?.previousTrends}
          xAxisDateFormat={xAxisDateFormat}
          isLoading={isLoading}
        />
        <div className="grid grid-cols-3 gap-4">
          {businessMetrics.map((metricName, i) => {
            const item = formattedAnalytics[metricName];
            return (
              <AnalyticsTrendsBox
                key={metricName + i}
                metricName={metricName}
                stat={item?.stat}
                currentTrends={item?.currentTrends ?? []}
                previousTrends={item?.previousTrends}
                isLoading={isLoading}
                xAxisDateFormat={xAxisDateFormat}
              />
            );
          })}
          {BUSINESS_BREAKDOWNS_ANALYTICS.map((metricName, i) => {
            const currentBreakdown = analytics.getBreakdowns.data?.find(
              (breakdown: BreakdownsResult) => breakdown.name === metricName
            );
            const previousBreakdown =
              previousAnalytics.getBreakdowns.data?.find(
                (breakdown: BreakdownsResult) => breakdown.name === metricName
              );
            return (
              <AnalyticsBarsBox
                key={metricName + i}
                isLoading={isLoading}
                metricName={metricName}
                currentBreakdown={
                  currentBreakdown
                    ? currentBreakdown
                    : {
                      name: metricName,
                      type: EStatisticType.NUMBER,
                      values: []
                    }
                }
                comparedBreakdown={previousBreakdown}
                dateRange={dateRange}
                comparedDateRange={previousDateRange}
              />
            );
          })}
        </div>
      </div>
    </Flex>
  );
};

export default AnalyticsBusinessTab;
